import { useFetcher, useRouteLoaderData } from "@remix-run/react";
import { AlertCircle, Gift, Loader2, Ticket, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/Input";
import { formatBookingDateWithWeekday, getCmsServiceFromList } from "~/lib/utils";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";
import CartItem from "./CartItem";
import VoucherCard from "./VoucherCard";

interface BookingCartSidebarProps {
  onRemoveItem?: (item: any, isMainService: boolean) => void;
  loadingRemove?: boolean;
  loadingAdd?: boolean;
  hasAddedToCartInThisFlow?: boolean;
  hideRemoveButtons?: boolean;
  hideTitle?: boolean;
  isVoucherBooking?: boolean;
  selectedDate?: Date;
  guestsCount?: { male: number; female: number };
  upsellingLoading?: boolean;
  onCompleteBooking?: () => void;
  showCompleteBookingButton?: boolean;
}

export default function BookingCartSidebar({
  onRemoveItem,
  loadingRemove = false,
  loadingAdd = false,
  hasAddedToCartInThisFlow = false,
  hideRemoveButtons = false,
  hideTitle = false,
  isVoucherBooking = false,
  selectedDate,
  guestsCount,
  upsellingLoading = false,
  onCompleteBooking,
  showCompleteBookingButton = false,
}: BookingCartSidebarProps) {
  const { t, i18n } = useTranslation();
  const {
    bookingCart,
    setBookingCart,
    removeVoucher,
    hasDifferentExpirationDates,
    appliedGiftCards,
    giftCardRemaining,
  } = useBookingCartStore();
  const rootData = useRouteLoaderData<any>("root");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [isMainServiceToDelete, setIsMainServiceToDelete] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState("");
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [discountWarning, setDiscountWarning] = useState<string | null>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountRemoving, setDiscountRemoving] = useState(false);
  const [discountCodeToRemove, setDiscountCodeToRemove] = useState<
    string | null
  >(null);
  const [discountRemoveConfirmOpen, setDiscountRemoveConfirmOpen] =
    useState(false);
  const [voucherToRemove, setVoucherToRemove] = useState<string | null>(null);
  const [voucherRemoveConfirmOpen, setVoucherRemoveConfirmOpen] =
    useState(false);
  const [voucherServices, setVoucherServices] = useState<
    Record<
      string,
      Array<{ code: string; title: string; shortDescription?: string }>
    >
  >({});
  const updateCartFetcher = useFetcher();
  const [isFetchingCart, setIsFetchingCart] = useState(false);
  const lastFetchedCartRef = useRef<any>(null);

  useEffect(() => {
    if (
      updateCartFetcher.state === "idle" &&
      updateCartFetcher.data &&
      (updateCartFetcher.data as any)?.Success &&
      !isFetchingCart &&
      updateCartFetcher.data !== lastFetchedCartRef.current
    ) {
      lastFetchedCartRef.current = updateCartFetcher.data;
      const fetchUpdatedCart = async () => {
        setIsFetchingCart(true);
        try {
          const cartResponse = await fetch("/api/booking/cart");
          if (cartResponse.ok) {
            const updatedCart = await cartResponse.json();
            const currentVouchers = bookingCart?.vouchers || [];
            setBookingCart({
              ...(updatedCart as any),
              vouchers: currentVouchers,
            });
          }
        } catch (error) {
          console.error("Failed to fetch updated cart:", error);
        } finally {
          setIsFetchingCart(false);
        }
      };
      fetchUpdatedCart();
    }
  }, [
    updateCartFetcher.state,
    updateCartFetcher.data,
    bookingCart?.vouchers,
    setBookingCart,
    isFetchingCart,
  ]);

  useEffect(() => {
    if (!bookingCart?.vouchers || !rootData?.servicesData) return;

    const servicesMap: Record<
      string,
      Array<{ code: string; title: string; shortDescription?: string }>
    > = {};

    bookingCart.vouchers.forEach((voucher) => {
      if (
        voucher.voucherData.ServicesIncludedCodes &&
        voucher.voucherData.ServicesIncludedCodes.length > 0 &&
        !voucherServices[voucher.code]
      ) {
        const includedServices: Array<{
          code: string;
          title: string;
          shortDescription?: string;
        }> = [];

        voucher.voucherData.ServicesIncludedCodes.forEach((serviceCode) => {
          const cmsService = getCmsServiceFromList(
            rootData.servicesData,
            serviceCode,
          );
          if (cmsService) {
            const codeObj = cmsService.acf?.codes?.find(
              (c: any) => c.code === serviceCode,
            );
            includedServices.push({
              code: serviceCode,
              title: cmsService.acf?.title || serviceCode,
              shortDescription: codeObj?.short_description,
            });
          } else {
            includedServices.push({
              code: serviceCode,
              title: serviceCode,
            });
          }
        });

        if (includedServices.length > 0) {
          servicesMap[voucher.code] = includedServices;
        }
      }
    });

    if (Object.keys(servicesMap).length > 0) {
      setVoucherServices((prev) => ({
        ...prev,
        ...servicesMap,
      }));
    }
  }, [bookingCart?.vouchers, rootData?.servicesData]);

  const handleDeleteClick = (item: any, isMain: boolean) => {
    if (upsellingLoading) return;
    setItemToDelete(item);
    setIsMainServiceToDelete(isMain);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (upsellingLoading) return;
    if (itemToDelete && onRemoveItem) {
      onRemoveItem(itemToDelete, isMainServiceToDelete);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const summary = useMemo(() => {
    if (
      !bookingCart ||
      !bookingCart.BasketItems ||
      bookingCart.BasketItems.length === 0
    ) {
      return {
        total: 0,
        currency: "",
        totalGuests: 0,
        netTotal: 0,
        discountItems: [] as Array<{
          Description?: string;
          Percentage?: number;
          Amount?: number;
        }>,
        totalDiscount: 0,
      };
    }

    const total = bookingCart.BasketItems.reduce((acc, item) => {
      if (item.Role === "INCLUDED") {
        return acc;
      }
      return acc + (item.GrossAmount?.Price || 0);
    }, 0);

    const netTotal = bookingCart.TotalNetAmount?.Price ?? total;
    const discountItems = bookingCart.DiscountItems || [];
    const totalDiscount = total - netTotal;
    const currency = bookingCart.BasketItems[0].GrossAmount?.Currency || "";

    return { total, currency, netTotal, discountItems, totalDiscount };
  }, [bookingCart]);

  const handleAddDiscountCode = async () => {
    if (!discountCodeInput.trim() || upsellingLoading) return;

    const currentCodes = bookingCart?.DiscountCodes || [];
    const codeToAdd = discountCodeInput.toUpperCase().trim();

    if (currentCodes.includes(codeToAdd)) {
      setDiscountError(t("Questo codice sconto è già stato applicato"));
      return;
    }

    const newCodes = [...currentCodes, codeToAdd];

    setDiscountLoading(true);
    setDiscountError(null);
    setDiscountWarning(null);

    try {
      const response = await fetch("/api/booking/apply-discount", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bookingCart: {
            BasketItems: bookingCart?.BasketItems || [],
          },
          discountCodes: newCodes,
        }),
      });

      const data = (await response.json()) as {
        error?: string;
        code?: number;
        warning?: boolean;
        response?: any;
        DiscountCodes?: string[];
        BasketItems?: any[];
        DiscountItems?: any[];
        TotalNetAmount?: any;
      };

      if (!response.ok || data.error) {
        if (data.code === 672) {
          setDiscountError(t("Il codice sconto non è valido"));
        } else {
          setDiscountError(
            data.error || t("Errore nell'applicazione del codice sconto"),
          );
        }
        setDiscountWarning(null);
      } else if (data.warning) {
        setDiscountWarning(
          t("Il codice sconto non è applicabile al carrello corrente"),
        );
        setDiscountError(null);
      } else if (data) {
        const responseData = data.response || data;
        const updatedCart = {
          ...bookingCart,
          BasketItems: responseData.BasketItems || bookingCart?.BasketItems,
          DiscountCodes: responseData.DiscountCodes,
          DiscountItems:
            responseData.DiscountItems || bookingCart?.DiscountItems,
          TotalNetAmount:
            responseData.TotalNetAmount || bookingCart?.TotalNetAmount,
        };
        setBookingCart(updatedCart);
        setDiscountCodeInput("");
        setDiscountError(null);
        setDiscountWarning(null);
      }
    } catch (error) {
      console.error("Failed to apply discount code:", error);
      setDiscountError(t("Errore nell'applicazione del codice sconto"));
      setDiscountWarning(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscountCode = async (codeToRemove: string) => {
    if (upsellingLoading) return;
    const currentCodes = bookingCart?.DiscountCodes || [];
    const newCodes = currentCodes.filter((code) => code !== codeToRemove);

    setDiscountRemoving(true);
    setDiscountError(null);
    setDiscountWarning(null);

    try {
      const response = await fetch("/api/booking/apply-discount", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bookingCart: {
            BasketItems: bookingCart?.BasketItems || [],
          },
          discountCodes: newCodes.length > 0 ? newCodes : [],
        }),
      });

      const data = (await response.json()) as {
        error?: string;
        code?: number;
        warning?: boolean;
        response?: any;
        DiscountCodes?: string[];
        BasketItems?: any[];
        DiscountItems?: any[];
        TotalNetAmount?: any;
      };

      if (!response.ok || data.error) {
        if (data.code === 672) {
          setDiscountError(t("Il codice sconto non è valido"));
        } else {
          setDiscountError(
            data.error || t("Errore nell'applicazione del codice sconto"),
          );
        }
        setDiscountWarning(null);
      } else if (data.warning) {
        setDiscountWarning(
          t("Il codice sconto non è applicabile al carrello corrente"),
        );
        setDiscountError(null);
      } else if (data) {
        const responseData = data.response || data;
        const updatedCart = {
          ...bookingCart,
          BasketItems: responseData.BasketItems || bookingCart?.BasketItems,
          DiscountCodes: responseData.DiscountCodes,
          DiscountItems:
            responseData.DiscountItems || bookingCart?.DiscountItems,
          TotalNetAmount:
            responseData.TotalNetAmount || bookingCart?.TotalNetAmount,
        };
        setBookingCart(updatedCart);
        setDiscountError(null);
        setDiscountWarning(null);
      }
    } catch (error) {
      console.error("Failed to remove discount code:", error);
      setDiscountError(t("Errore nell'applicazione del codice sconto"));
      setDiscountWarning(null);
    } finally {
      setDiscountRemoving(false);
    }
  };

  const handleRemoveVoucher = (code: string) => {
    if (upsellingLoading) return;
    setVoucherToRemove(code);
    setVoucherRemoveConfirmOpen(true);
  };

  const confirmRemoveVoucher = () => {
    if (upsellingLoading) return;
    if (voucherToRemove && bookingCart) {
      const updatedBasketItems = bookingCart.BasketItems?.map((item: any) => {
        if (item.VoucherCode === voucherToRemove) {
          const updatedItem = { ...item };
          delete updatedItem.VoucherCode;
          delete updatedItem.GiftCardType;
          return updatedItem;
        }
        return item;
      });

      const updatedVouchers = (bookingCart.vouchers || []).filter(
        (v) => v.code !== voucherToRemove,
      );

      removeVoucher(voucherToRemove);

      if (updatedBasketItems && updatedBasketItems.length > 0) {
        const { vouchers, ...cartWithoutVouchers } = bookingCart;
        const updatedCart = {
          ...cartWithoutVouchers,
          BasketItems: updatedBasketItems,
          ...(bookingCart?.DiscountCodes && {
            DiscountCodes: bookingCart?.DiscountCodes,
          }),
        };

        setBookingCart({
          ...updatedCart,
          vouchers: updatedVouchers.length > 0 ? updatedVouchers : undefined,
        });

        updateCartFetcher.submit(updatedCart as any, {
          method: "post",
          action: "/api/booking/add-to-cart",
          encType: "application/json",
        });
      }

      setVoucherToRemove(null);
      setVoucherRemoveConfirmOpen(false);
    }
  };

  const hasItems =
    bookingCart &&
    bookingCart.BasketItems &&
    bookingCart.BasketItems.length > 0;

  const mainServices: any[] = [];
  const upsellingServices: any[] = [];
  const groupedItems: Record<number, { main: any; included: any[] }> = {};

  bookingCart?.BasketItems?.forEach((item) => {
    if (item.Role === "INTEGRATION") {
      upsellingServices.push(item);
    } else if (item.Role !== "INCLUDED") {
      const lineNr = item.LineNr || 0;
      if (!groupedItems[lineNr]) {
        groupedItems[lineNr] = {
          main: item,
          included: [],
        };
      }
    }
  });

  bookingCart?.BasketItems?.forEach((item) => {
    if (item.Role === "INCLUDED" && item.RefLine) {
      if (groupedItems[item.RefLine]) {
        groupedItems[item.RefLine].included.push(item);
      }
    }
  });

  const mainItemsMap = new Map<
    string,
    {
      main: any;
      included: any[];
      quantity: number;
      totalPrice: number;
      voucherCode?: string;
      voucherCodes: string[];
      voucherCount: number;
    }
  >();

  Object.values(groupedItems).forEach((group) => {
    if (group.main.Role === "MAIN") {
      const key = `${group.main.Service}-${group.main.CheckinDate}-${group.main.StartTime}`;
      const itemVoucher = (group.main as any).VoucherCode;
      const numPax = (group.main as any).ServiceNumPax || 1;
      const itemQuantity = (group.main.Quantity?.Total || 1) * numPax;

      if (mainItemsMap.has(key)) {
        const existing = mainItemsMap.get(key)!;
        existing.quantity += itemQuantity;
        existing.totalPrice += group.main.GrossAmount?.Price || 0;
        existing.included.push(...group.included);
        if (itemVoucher) {
          if (!existing.voucherCode) {
            existing.voucherCode = itemVoucher;
          }
          if (!existing.voucherCodes.includes(itemVoucher)) {
            existing.voucherCodes.push(itemVoucher);
          }
          existing.voucherCount++;
        }
      } else {
        mainItemsMap.set(key, {
          main: group.main,
          included: group.included,
          quantity: itemQuantity,
          totalPrice: group.main.GrossAmount?.Price || 0,
          voucherCode: itemVoucher,
          voucherCodes: itemVoucher ? [itemVoucher] : [],
          voucherCount: itemVoucher ? 1 : 0,
        });
      }
    }
  });

  const processedLineNrs = new Set(Object.keys(groupedItems).map(Number));
  const standaloneItems =
    bookingCart?.BasketItems?.filter(
      (item) => item.Role === "MAIN" && !processedLineNrs.has(item.LineNr || 0),
    ) || [];

  standaloneItems.forEach((item) => {
    const key = `${item.Service}-${item.CheckinDate}-${item.StartTime}`;
    const itemVoucher = (item as any).VoucherCode;
    const numPax = (item as any).ServiceNumPax || 1;
    const itemQuantity = (item.Quantity?.Total || 1) * numPax;

    if (mainItemsMap.has(key)) {
      const existing = mainItemsMap.get(key)!;
      existing.quantity += itemQuantity;
      existing.totalPrice += item.GrossAmount?.Price || 0;
      if (itemVoucher) {
        if (!existing.voucherCode) {
          existing.voucherCode = itemVoucher;
        }
        if (!existing.voucherCodes.includes(itemVoucher)) {
          existing.voucherCodes.push(itemVoucher);
        }
        existing.voucherCount++;
      }
    } else {
      mainItemsMap.set(key, {
        main: item,
        included: [],
        quantity: itemQuantity,
        totalPrice: item.GrossAmount?.Price || 0,
        voucherCode: itemVoucher,
        voucherCodes: itemVoucher ? [itemVoucher] : [],
        voucherCount: itemVoucher ? 1 : 0,
      });
    }
  });

  mainServices.push(...Array.from(mainItemsMap.values()));

  return (
    <>
      <div className="w-full lg:w-96 lg:border-l border-gray-200 lg:pl-6 pr-4 py-4 overflow-y-auto relative">
        {hideTitle && (
          <div className="lg:hidden pb-4">
            {(() => {
              const mainService = mainServices?.[0]?.main;
              const mainServiceDate = mainService?.CheckinDate
                ? formatBookingDateWithWeekday(mainService.CheckinDate, i18n.language)
                : null;

              const selectedDateFormatted = selectedDate
                ? formatBookingDateWithWeekday(selectedDate, i18n.language)
                : null;

              const displayDate = mainServiceDate || selectedDateFormatted;

              const maleCountFromCart =
                bookingCart?.BasketItems?.reduce(
                  (acc: number, item: any) =>
                    item.Role !== "INCLUDED"
                      ? acc + (item.Quantity?.Man || 0)
                      : acc,
                  0,
                ) || 0;
              const femaleCountFromCart =
                bookingCart?.BasketItems?.reduce(
                  (acc: number, item: any) =>
                    item.Role !== "INCLUDED"
                      ? acc + (item.Quantity?.Woman || 0)
                      : acc,
                  0,
                ) || 0;

              const maleCount = maleCountFromCart || guestsCount?.male || 0;
              const femaleCount =
                femaleCountFromCart || guestsCount?.female || 0;

              return (
                <div className="space-y-2">
                  {displayDate && (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-700">
                        {t("Data")}:
                      </span>
                      <span className="text-lg text-gray-600 bg-secondary rounded-sm px-2 py-0.5 text-secondary-foreground font-semibold whitespace-nowrap">
                        {displayDate}
                      </span>
                    </div>
                  )}
                  {maleCount > 0 || femaleCount > 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-700">
                        {t("Ospiti")}:
                      </span>
                      <span className="text-lg text-secondary font-bold">
                        {maleCount > 0 && (
                          <>
                            {maleCount}{" "}
                            {maleCount === 1 ? t("uomo") : t("uomini")}
                          </>
                        )}
                        {maleCount > 0 && femaleCount > 0 && " + "}
                        {femaleCount > 0 && (
                          <>
                            {femaleCount}{" "}
                            {femaleCount === 1 ? t("donna") : t("donne")}
                          </>
                        )}
                      </span>
                    </div>
                  ) : null}
                </div>
              );
            })()}
          </div>
        )}
        {!hideTitle && (
          <>
            {(() => {
              const mainService = mainServices?.[0]?.main;
              const mainServiceDate = mainService?.CheckinDate
                ? formatBookingDateWithWeekday(mainService.CheckinDate, i18n.language)
                : null;

              const selectedDateFormatted = selectedDate
                ? formatBookingDateWithWeekday(selectedDate, i18n.language)
                : null;

              const displayDate = mainServiceDate || selectedDateFormatted;

              const maleCountFromCart =
                bookingCart?.BasketItems?.reduce(
                  (acc: number, item: any) =>
                    item.Role !== "INCLUDED"
                      ? acc + (item.Quantity?.Man || 0)
                      : acc,
                  0,
                ) || 0;
              const femaleCountFromCart =
                bookingCart?.BasketItems?.reduce(
                  (acc: number, item: any) =>
                    item.Role !== "INCLUDED"
                      ? acc + (item.Quantity?.Woman || 0)
                      : acc,
                  0,
                ) || 0;

              const maleCount = maleCountFromCart || guestsCount?.male || 0;
              const femaleCount =
                femaleCountFromCart || guestsCount?.female || 0;

              return (
                <>
                  <div className="flex justify-between items-center">
                    <h4 className="text-xl font-semibold">
                      {t("Riepilogo prenotazione")}
                    </h4>
                    {displayDate && (
                      <span className="animate-in fade-in-0 duration-300 bg-secondary rounded-sm px-2 py-0.5 text-md text-secondary-foreground font-semibold whitespace-nowrap shrink-0">
                        {displayDate}
                      </span>
                    )}
                  </div>
                  {maleCount > 0 || femaleCount > 0 ? (
                    <p className="animate-in fade-in-0 duration-300 text-md text-secondary font-bold mb-0">
                      {maleCount > 0 && (
                        <>
                          {maleCount}{" "}
                          {maleCount === 1 ? t("uomo") : t("uomini")}
                        </>
                      )}
                      {maleCount > 0 && femaleCount > 0 && " + "}
                      {femaleCount > 0 && (
                        <>
                          {femaleCount}{" "}
                          {femaleCount === 1 ? t("donna") : t("donne")}
                        </>
                      )}
                    </p>
                  ) : null}
                </>
              );
            })()}
          </>
        )}
        {(loadingAdd ||
          updateCartFetcher.state === "submitting" ||
          isFetchingCart ||
          upsellingLoading) && (
            <div className="animate-in fade-in-0 duration-300 absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-2">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-secondary border-t-transparent"></div>
                <span className="text-sm text-gray-600">
                  {t("Aggiorno il carrello...")}
                </span>
              </div>
            </div>
          )}
        <div className="rounded-lg pb-4 lg:pt-3 space-y-3">
          {!hasAddedToCartInThisFlow || !hasItems ? (
            <div className="">
              <p className="text-gray-400">
                {t("Nessun servizio selezionato")}
              </p>
              {isVoucherBooking &&
                bookingCart?.vouchers &&
                bookingCart.vouchers.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 text-left">
                    <div className="flex flex-row justify-between items-start mb-2">
                      <h5 className="font-semibold text-md">
                        {t("Voucher disponibili")}
                      </h5>
                      <div className="flex items-center gap-2">
                        {hasDifferentExpirationDates() && (
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                        )}
                        {updateCartFetcher.state === "submitting" && (
                          <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                        )}
                      </div>
                    </div>
                    {hasDifferentExpirationDates() && (
                      <p className="text-xs text-amber-600 mb-2">
                        {t("Attenzione: date di scadenza diverse")}
                      </p>
                    )}
                    <div className="space-y-3">
                      {(() => {
                        const voucherCodesInCart = new Set(
                          bookingCart.BasketItems?.map(
                            (item: any) => item.VoucherCode,
                          ).filter(Boolean) || [],
                        );
                        return bookingCart.vouchers.map((voucher) => (
                          <VoucherCard
                            key={voucher.code}
                            voucher={voucher}
                            rootData={rootData}
                            i18n={i18n}
                            isMatched={voucherCodesInCart.has(voucher.code)}
                          />
                        ));
                      })()}
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <>
              {mainServices.length > 0 && (
                <div className="mb-2 border-t border-gray-200 pt-2">
                  <h5 className="font-semibold text-md mb-0">
                    {t("Servizi principali")}
                  </h5>
                  <div>
                    {mainServices.map((group, groupIndex) => {
                      const cmsService = getCmsServiceFromList(
                        rootData?.servicesData || [],
                        group.main.Service,
                      );
                      const serviceShortDescription =
                        cmsService?.acf?.codes.find(
                          (c: any) => c.code === group.main.Service,
                        )?.short_description;

                      const groupKey = `${group.main.Service}-${group.main.CheckinDate}-${group.main.StartTime}-${groupIndex}`;

                      if (group.included.length > 0) {
                        return (
                          <Accordion key={groupKey} type="single" collapsible>
                            <AccordionItem value={`item-${groupIndex}`}>
                              <AccordionTrigger className="pb-6 hover:no-underline">
                                <div className="flex flex-row justify-between w-full pr-4">
                                  <div className="flex flex-col flex-1 text-left">
                                    <span className="font-medium">
                                      {cmsService
                                        ? cmsService.acf.title
                                        : group.main.Service}
                                      {group.quantity > 1 && (
                                        <span className="ml-2 text-sm font-bold">
                                          x {group.quantity}
                                        </span>
                                      )}
                                    </span>
                                    {serviceShortDescription && (
                                      <span className="text-sm text-gray-500">
                                        {serviceShortDescription}
                                        {group.main.StartTime &&
                                          !group.main.StartTime.startsWith(
                                            "00:00:00",
                                          ) &&
                                          !group.main.EndTime?.startsWith(
                                            "23:59:59",
                                          ) &&
                                          `, ${group.main.StartTime.slice(
                                            0,
                                            5,
                                          )}${group.main.EndTime &&
                                            !group.main.EndTime.startsWith(
                                              "23:59:59",
                                            )
                                            ? ` - ${group.main.EndTime.slice(
                                              0,
                                              5,
                                            )}`
                                            : ""
                                          }`}
                                      </span>
                                    )}
                                    {!serviceShortDescription &&
                                      group.main.StartTime &&
                                      !group.main.StartTime.startsWith(
                                        "00:00:00",
                                      ) &&
                                      !group.main.EndTime?.startsWith(
                                        "23:59:59",
                                      ) && (
                                        <span className="text-sm text-gray-500">
                                          {group.main.StartTime.slice(0, 5)}
                                          {group.main.EndTime &&
                                            !group.main.EndTime.startsWith(
                                              "23:59:59",
                                            ) &&
                                            ` - ${group.main.EndTime.slice(
                                              0,
                                              5,
                                            )}`}
                                        </span>
                                      )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {(group.voucherCodes?.length ?? 0) > 0 ? (
                                      <div className="flex items-center gap-1 group relative">
                                        <span className="text-primary font-semibold whitespace-nowrap">
                                          {t("Incluso")}
                                        </span>
                                        <div className="relative">
                                          <Ticket
                                            size={16}
                                            className="text-green-600 cursor-help hover:text-gray-600 transition-colors mt-[1px]"
                                          />
                                          <div className="absolute bottom-full right-0 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-56 bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-xl pointer-events-none">
                                            {group.voucherCodes?.length > 1
                                              ? t("Incluso con i voucher") +
                                              " " +
                                              group.voucherCodes.join(", ")
                                              : t("Incluso con il voucher") +
                                              " " +
                                              ((group.main as any)
                                                .VoucherCode ||
                                                group.voucherCode ||
                                                "")}
                                            <div className="absolute top-full right-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 group relative">
                                        <span className="font-semibold whitespace-nowrap">
                                          €{" "}
                                          {group.totalPrice.toLocaleString(
                                            i18n.language,
                                            {
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 2,
                                            },
                                          )}
                                        </span>
                                        {(group.voucherCodes?.length ?? 0) >
                                          0 && (
                                            <div className="relative flex items-center gap-1">
                                              <Ticket
                                                size={16}
                                                className="text-green-600 cursor-help hover:text-gray-600 transition-colors"
                                              />
                                              {group.voucherCount > 1 && (
                                                <span className="text-xs text-gray-500">
                                                  {group.voucherCount}
                                                </span>
                                              )}
                                              <div className="absolute bottom-full right-0 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-56 bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-xl pointer-events-none">
                                                {group.voucherCodes?.length > 1
                                                  ? t("Incluso con i voucher") +
                                                  " " +
                                                  group.voucherCodes.join(", ")
                                                  : group.voucherCount +
                                                  " " +
                                                  (group.voucherCount === 1
                                                    ? t("incluso")
                                                    : t("inclusi")) +
                                                  " " +
                                                  t("con il voucher") +
                                                  " " +
                                                  (group.voucherCodes?.[0] ??
                                                    (group.main as any)
                                                      .VoucherCode ??
                                                    group.voucherCode ??
                                                    "")}
                                                <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                    {onRemoveItem && !hideRemoveButtons && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteClick(group.main, true);
                                        }}
                                        disabled={
                                          loadingRemove || upsellingLoading
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="space-y-2 pt-2 pl-4">
                                  {group.included.map(
                                    (includedItem: any, idx: number) => {
                                      const includedCmsService =
                                        getCmsServiceFromList(
                                          rootData?.servicesData || [],
                                          includedItem.Service,
                                        );
                                      const includedServiceShortDescription =
                                        includedCmsService?.acf?.codes.find(
                                          (c: any) =>
                                            c.code === includedItem.Service,
                                        )?.short_description;

                                      const includedKey = `${includedItem.Service}-${includedItem.CheckinDate}-${includedItem.StartTime}-${idx}`;

                                      return (
                                        <div
                                          key={includedKey}
                                          className="flex flex-row justify-between border-b border-gray-100 last:border-none pb-2"
                                        >
                                          <div className="flex flex-col flex-1">
                                            <span className="text-sm font-medium">
                                              {includedCmsService
                                                ? includedCmsService.acf.title
                                                : includedItem.Service}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              {includedServiceShortDescription
                                                ? includedServiceShortDescription +
                                                ", "
                                                : null}
                                              {includedItem.CheckinDate &&
                                                formatBookingDateWithWeekday(
                                                  includedItem.CheckinDate,
                                                  i18n.language,
                                                )}
                                              {includedItem.StartTime &&
                                                !includedItem.StartTime.startsWith(
                                                  "00:00:00",
                                                ) &&
                                                !includedItem.EndTime?.startsWith(
                                                  "23:59:59",
                                                ) &&
                                                `, ${includedItem.StartTime.slice(
                                                  0,
                                                  5,
                                                )}${includedItem.EndTime &&
                                                  !includedItem.EndTime.startsWith(
                                                    "23:59:59",
                                                  )
                                                  ? ` - ${includedItem.EndTime.slice(
                                                    0,
                                                    5,
                                                  )}`
                                                  : ""
                                                }`}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-sm font-semibold text-secondary">
                                              {t("INCLUSO")}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    },
                                  )}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        );
                      } else {
                        const cmsService = getCmsServiceFromList(
                          rootData?.servicesData || [],
                          group.main.Service,
                        );
                        const serviceShortDescription =
                          cmsService?.acf?.codes.find(
                            (c: any) => c.code === group.main.Service,
                          )?.short_description;

                        const groupKey = `${group.main.Service}-${group.main.CheckinDate}-${group.main.StartTime}-${groupIndex}`;

                        return (
                          <CartItem
                            key={groupKey}
                            item={group.main}
                            cmsService={cmsService}
                            serviceShortDescription={serviceShortDescription}
                            quantity={group.quantity}
                            voucherCount={group.voucherCount}
                            totalPrice={group.totalPrice}
                            voucherCode={group.voucherCode}
                            voucherCodes={group.voucherCodes}
                            onRemove={
                              onRemoveItem ? handleDeleteClick : undefined
                            }
                            isMainService={true}
                            loadingRemove={loadingRemove}
                            hideRemoveButtons={hideRemoveButtons}
                            upsellingLoading={upsellingLoading}
                            i18n={i18n}
                          />
                        );
                      }
                    })}
                  </div>
                </div>
              )}

              {upsellingServices.length > 0 && (
                <div>
                  <h5 className="font-semibold text-md mb-0">
                    {t("Servizi aggiuntivi")}
                  </h5>
                  {upsellingServices.map((item, index) => {
                    const cmsService = getCmsServiceFromList(
                      rootData?.servicesData || [],
                      item.Service,
                    );
                    const serviceShortDescription = cmsService?.acf?.codes.find(
                      (c: any) => c.code === item.Service,
                    )?.short_description;

                    const itemKey = `${item.Service}-${item.CheckinDate}-${item.StartTime
                      }-${item.BasketItemId || index}`;

                    return (
                      <CartItem
                        key={itemKey}
                        item={item}
                        cmsService={cmsService}
                        serviceShortDescription={serviceShortDescription}
                        onRemove={onRemoveItem ? handleDeleteClick : undefined}
                        isMainService={false}
                        loadingRemove={loadingRemove}
                        hideRemoveButtons={hideRemoveButtons}
                        upsellingLoading={upsellingLoading}
                        showDate={true}
                        i18n={i18n}
                      />
                    );
                  })}
                </div>
              )}

              {isVoucherBooking &&
                bookingCart?.vouchers &&
                bookingCart.vouchers.length > 0 && (
                  <div className="mt-4">
                    <div className="flex flex-row justify-between items-start mb-2">
                      <h5 className="font-semibold text-md">
                        {t("Voucher disponibili")}
                      </h5>
                      <div className="flex items-center gap-2">
                        {hasDifferentExpirationDates() && (
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                        )}
                        {updateCartFetcher.state === "submitting" && (
                          <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                        )}
                      </div>
                    </div>
                    {hasDifferentExpirationDates() && (
                      <p className="text-xs text-amber-600 mb-2">
                        {t("Attenzione: date di scadenza diverse")}
                      </p>
                    )}
                    <div className="space-y-3">
                      {(() => {
                        const voucherCodesInCart = new Set(
                          bookingCart.BasketItems?.map(
                            (item: any) => item.VoucherCode,
                          ).filter(Boolean) || [],
                        );

                        return (bookingCart.vouchers || []).map((voucher) => {
                          const isVoucherMatched = voucherCodesInCart.has(
                            voucher.code,
                          );

                          return (
                            <div
                              key={voucher.code}
                              className={
                                updateCartFetcher.state === "submitting" &&
                                  voucherToRemove === voucher.code
                                  ? "opacity-50 relative"
                                  : ""
                              }
                            >
                              {updateCartFetcher.state === "submitting" &&
                                voucherToRemove === voucher.code && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-xl z-10">
                                    <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                                  </div>
                                )}
                              <VoucherCard
                                voucher={voucher}
                                rootData={rootData}
                                isMatched={isVoucherMatched}
                                i18n={i18n}
                              />
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

              <div className="mt-0">
                <h5 className="font-semibold text-md mb-3">
                  {t("Codice promozionale")}
                </h5>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Input
                      disabled={discountLoading || upsellingLoading}
                      type="text"
                      placeholder={t("Inserisci codice sconto")}
                      value={discountCodeInput}
                      onChange={(e) => {
                        setDiscountCodeInput(e.target.value.toUpperCase());
                        setDiscountError(null);
                        setDiscountWarning(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddDiscountCode();
                        }
                      }}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddDiscountCode}
                      disabled={
                        !discountCodeInput.trim() ||
                        discountLoading ||
                        upsellingLoading
                      }
                      className="relative"
                    >
                      {discountLoading && (
                        <span className="absolute left-0 w-full h-full flex items-center justify-center top-0">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
                        </span>
                      )}
                      <span className={discountLoading ? "opacity-50" : ""}>
                        {t("Applica")}
                      </span>
                    </Button>
                  </div>
                  {discountError && (
                    <p className="text-destructive text-sm">{discountError}</p>
                  )}
                  {discountWarning && (
                    <p className="text-amber-600 text-sm">{discountWarning}</p>
                  )}
                </div>
                {bookingCart?.DiscountCodes &&
                  bookingCart.DiscountCodes.length > 0 && (
                    <div className="mt-1 pt-1">
                      <div className="flex flex-row justify-between items-center gap-2">
                        <div className="flex flex-col min-w-0">
                          {bookingCart?.DiscountCodes &&
                            bookingCart.DiscountCodes.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {bookingCart.DiscountCodes.map(
                                  (code: string) => (
                                    <div
                                      key={code}
                                      className="flex items-center gap-1 bg-secondary rounded-sm px-2 py-1"
                                    >
                                      <span className="text-xs text-secondary-foreground">
                                        {code}
                                      </span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-4 w-4 text-secondary-foreground hover:text-secondary-foreground-hover hover:bg-secondary-foreground-hover p-0"
                                        onClick={() => {
                                          setDiscountCodeToRemove(code);
                                          setDiscountRemoveConfirmOpen(true);
                                        }}
                                        disabled={
                                          discountRemoving ||
                                          discountLoading ||
                                          upsellingLoading
                                        }
                                      >
                                        {discountRemoving ? (
                                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-solid border-white border-t-transparent"></div>
                                        ) : (
                                          <X className="h-3 w-3" />
                                        )}
                                      </Button>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                        </div>
                        {summary.totalDiscount > 0 && (
                          <span className="text-md font-semibold text-green-600 whitespace-nowrap flex-shrink-0">
                            -€{" "}
                            {summary.totalDiscount.toLocaleString(
                              i18n.language,
                              {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
              </div>

              <div className="flex flex-row justify-between border-t border-gray-200 pt-2 !mt-4">
                <div className="flex flex-col">
                  <span className="font-bold text-xl">
                    {t("Totale (IVA inclusa)")}
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  {summary.netTotal < summary.total ? (
                    <>
                      <span className="font-bold text-xl text-green-600">
                        € {summary.netTotal.toLocaleString(i18n.language)}
                      </span>
                      <span className="font-bold text-xs line-through text-gray-400">
                        € {summary.total.toLocaleString(i18n.language)}
                      </span>
                    </>
                  ) : (
                    <span className="font-bold text-xl">
                      € {summary.total.toLocaleString(i18n.language)}
                    </span>
                  )}
                </div>
              </div>

              {appliedGiftCards.length > 0 && (
                <div className="flex flex-col gap-2 border-t border-gray-200 pt-2 !mt-2">
                  <div className="flex flex-row justify-between items-start gap-2">
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Gift size={14} className="text-secondary flex-shrink-0" />
                        {t("Anticipo pagato con gift card")}
                      </span>
                      {appliedGiftCards.map((card) => (
                        <span
                          key={card.id}
                          className="text-xs text-gray-400 pl-5"
                        >
                          {card.maskedCardCode}
                        </span>
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-green-600 whitespace-nowrap flex-shrink-0">
                      -€{" "}
                      {appliedGiftCards
                        .reduce((acc, card) => acc + card.amount, 0)
                        .toLocaleString(i18n.language)}
                    </span>
                  </div>
                  {giftCardRemaining !== null && (
                    <div className="flex flex-row justify-between items-center gap-2">
                      <span className="text-sm font-semibold">
                        {t("Da pagare")}
                      </span>
                      <span className="text-sm font-semibold">
                        € {giftCardRemaining.toLocaleString(i18n.language)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {showCompleteBookingButton && onCompleteBooking && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              className="w-full"
              onClick={onCompleteBooking}
              disabled={loadingAdd || loadingRemove || upsellingLoading}
            >
              {t("Concludi Prenotazione")}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isMainServiceToDelete
                ? t("Elimina servizio principale")
                : t("Elimina servizio aggiuntivo")}
            </DialogTitle>
            <DialogDescription>
              {isMainServiceToDelete ? (
                <span>
                  {t("Sei sicuro di voler eliminare questo servizio?")}
                  <br />
                  {t(
                    "Questa azione ti riporterà all'inizio del flusso di prenotazione e svuoterà il carrello.",
                  )}
                </span>
              ) : (
                <span>
                  {t(
                    "Sei sicuro di voler eliminare questo servizio aggiuntivo?",
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div className="flex flex-row justify-between w-full">
              <Button
                className="flex-1"
                variant="muted"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setItemToDelete(null);
                }}
              >
                {t("Annulla")}
              </Button>
              <Button
                className="flex-1 ml-4"
                variant="secondary"
                onClick={confirmDelete}
                disabled={loadingRemove || upsellingLoading}
              >
                {loadingRemove ? t("Eliminazione...") : t("Elimina")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={discountRemoveConfirmOpen}
        onOpenChange={setDiscountRemoveConfirmOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Rimuovi codice sconto")}</DialogTitle>
            <DialogDescription>
              {t("Sei sicuro di voler rimuovere il codice sconto")}{" "}
              {discountCodeToRemove}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="muted"
              onClick={() => {
                setDiscountRemoveConfirmOpen(false);
                setDiscountCodeToRemove(null);
              }}
            >
              {t("Annulla")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (discountCodeToRemove) {
                  handleRemoveDiscountCode(discountCodeToRemove);
                  setDiscountRemoveConfirmOpen(false);
                  setDiscountCodeToRemove(null);
                }
              }}
              disabled={discountRemoving || upsellingLoading}
            >
              {t("Conferma")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={voucherRemoveConfirmOpen}
        onOpenChange={setVoucherRemoveConfirmOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Rimuovi voucher")}</DialogTitle>
            <DialogDescription>
              {t("Sei sicuro di voler rimuovere il voucher")} {voucherToRemove}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="muted"
              onClick={() => {
                setVoucherRemoveConfirmOpen(false);
                setVoucherToRemove(null);
              }}
            >
              {t("Annulla")}
            </Button>
            <Button
              variant="secondary"
              onClick={confirmRemoveVoucher}
              disabled={upsellingLoading}
            >
              {t("Conferma")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
